import React, { useEffect, useState } from "react";
import { Box, TextField, useMediaQuery } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Button } from "react-bootstrap";
import { Http, Regex } from "../../../utils/constants";
import {
  fetchChangeEmail,
  fetchEmailChange,
  fetchSendMailVerify,
  fetchUserInfo,
} from "../../../utils/helpers/api";
import { useGlobalState } from "../../../contexts/GlobalContext";
import { AxiosResponse } from "axios";
import DangerNotify from "../../../components/svg/DangerNotify";
import ErrorMsg from "../../../components/Commons/ErrorMsg";

const EmailSetting: React.FC = () => {
  const { t } = useTranslation();
  const { setStateValue, globalState } = useGlobalState();
  const { user } = globalState;
  const user_id = user?.user_id;
  const isSmallScreen = useMediaQuery("(max-width:756px)");
  const [state, setState] = useState<ISettingEmailPageState>({
    email: "",
    email_confirm: "",
    type: 1,
    current_email: "",
    email_verified: true,
  });
  const [error, setError] = useState({
    email: "",
    email_confirm: "",
  });

  useEffect(() => {
    document.title = '宣伝会議 : アカウント管理 | メールアドレス設定';
  }, []);

  useEffect(() => {
    if (user_id) {
      handleFetchUserInfo();
    }
  }, [user_id]);

  const handleChangeValue = (field: string, value?: string) => {
    setState((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  const handleSubmit = async () => {
    let isValidate = true;
    let errorEmailMess = "";
    let errorEmailConfirmMess = "";
    if (!state.email) {
      isValidate = false;
      errorEmailMess = t("validation.emailFieldRequiredMessage");
    } else if (!Regex.RULE.EMAIL.test(state.email)) {
      isValidate = false;
      errorEmailMess = t("validation.emailInvalidMessage");
    } else if (state.email.length > 74) {
      isValidate = false;
      errorEmailMess = t("validation.emailAddressLimit");
    } else {
      errorEmailMess = "";
    }

    if (!state.email_confirm) {
      isValidate = false;
      errorEmailConfirmMess = t("validation.emailConfirmationRequired");
    } else if (state.email !== state.email_confirm) {
      errorEmailConfirmMess = t("validation.emailConfirmationMismatch");
      isValidate = false;
    } else {
      errorEmailConfirmMess = "";
    }

    setError({
      email: errorEmailMess,
      email_confirm: errorEmailConfirmMess,
    });
    if (isValidate) {
      setStateValue("loader", true);
      try {
        // const result = await fetchChangeEmail(user_id ?? "", state.email);
        const lowerCaseEmail = state.email.toLowerCase();

        const result = await fetchEmailChange(user_id ?? "", lowerCaseEmail);
        if (result?.status === Http.SUCCESS_CODE) {
          setState((prevState) => ({
            ...prevState,
            email_verified: false,
          }));
          // await fetchSendMailVerify(user_id ?? "");
          document.title = '宣伝会議 : アカウント管理 | メールアドレス変更の手続き中です';
        } else {
          errorEmailMess = t("validation.emailAlreadyRegistered");
          setError((prevState) => ({
            ...prevState,
            email: errorEmailMess,
          }));
          setState((prevState) => ({
            ...prevState,
            type: 1,
          }));
        }
      } catch (error) {
        if (!(error instanceof Error)) {
          const res = error as AxiosResponse;
          if(res.status == 422 && res.data.code == "EMAIL_ALREADY_EXISTS") {
            errorEmailMess = t("validation.emailAlreadyRegistered");
            setError((prevState) => ({
              ...prevState,
              email: errorEmailMess,
            }));
            setState((prevState) => ({
              ...prevState,
              type: 1,
            }));
          }
          return res;
        }
        return error;
      } finally {
        setStateValue("loader", false);
      }
    }
  };

  const handleFetchUserInfo = async () => {
    setStateValue("loader", true);
    try {
      // const result = await fetchUserInfo(user_id ?? "");
      const { email, email_verified } = globalState.user
      setState((prevState) => ({
        ...prevState,
        current_email: email ?? "",
        email_verified: email_verified ?? false,
      }));
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse;
        return res;
      }
    } finally {
      setStateValue("loader", false);
    }
  };

  return (
    <Box className="page__setting-mail">
      {state.email_verified ? (
          <div>
            <h1 className="header-tit">{t("features.emailSetting.headTit")}</h1>
            <p className="mt-3">
              {t("features.emailSetting.currentEmailText.part1")}
              <span className="txt-loud wrap">"{state.current_email}"</span>
              {t("features.emailSetting.currentEmailText.part2")}
              <br />
              {t("features.emailSetting.currentEmailText.part3")}
            </p>
            <p className="mt-3">
              {t("features.emailSetting.confirmText.part1")}
              <br />
              {t("features.emailSetting.confirmText.part2")}
            </p>
            <p className="mt-3">
              {t("features.emailSetting.noConfirmationEmailGuidance")}
              <br />
              {t("features.emailSetting.emailDelayNotice")}
              <br />
              {t("features.emailSetting.emailSpamNotice")}
            </p>

            <hr />
            <div className="form-horizontal">
              <div className="form-group row">
                <div className=" col-sm-4 form-label">
                  <label>{t("features.emailSetting.newEmailAddress")}</label>
                </div>
                <div className="col-sm-8">
                  <TextField
                    type="email"
                    id="email"
                    value={state.email}
                    fullWidth={isSmallScreen}
                    autoComplete="email"
                    className="w-100"
                    placeholder="taro@example.com"
                    size="small"
                    inputProps={{
                      className: "form-control mt4",
                      style: { height: 15 },
                      "aria-hidden": false,
                      title: t("general.email"),
                    }}
                    onChange={(e) => handleChangeValue("email", e.target.value)}
                  />
                  <ErrorMsg
                    error={error.email}
                  />
                </div>
              </div>

              <div>
                <div className="form-group row mt-2">
                  <div className="col-sm-4 form-label">
                    <label>
                      {t("features.emailSetting.newEmailConfirmation")}
                    </label>
                  </div>

                  <div className="col-sm-8 ">
                    <TextField
                      type="email"
                      id="email_confirm"
                      name="email_confirm"
                      autoComplete="email"
                      fullWidth={isSmallScreen}
                      className="w-100"
                      value={state.email_confirm}
                      placeholder="taro@example.com"
                      onChange={(e) =>
                        handleChangeValue("email_confirm", e.target.value)
                      }
                      size="small"
                      inputProps={{
                        className: "form-control mt4",
                        style: { height: 15 },
                        "aria-hidden": false,
                        title: t("general.email"),
                      }}
                      FormHelperTextProps={{
                        sx: { marginLeft: 0 },
                      }}
                    />
                    <ErrorMsg
                      error={error.email_confirm}
                    />
                    <p className="txt-sm txt-gray mt8">
                      {t("features.emailSetting.reenterEmailPrompt")}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <hr />

            <Box className="center mt-4">
              <Button
                onClick={handleSubmit}
                className="btn btn-primary form-control btn-md btn-submit"
              >
                {t("common.buttonTextSend")}
              </Button>
            </Box>
          </div>
      ) : (
        <div>
          <h1 className="header-tit">
            {t("features.emailSetting.inProgressTit")}
          </h1>
          <p className="mt-3">
            {t("features.emailSetting.inProgess.emailSubjectNotification")}
            <br />
            {t("features.emailSetting.inProgess.notCompletedText")}
          </p>

          <p className="mt-3">
            <span>
              {t("features.emailSetting.inProgess.currentEmail")}"
              {state.current_email}"
            </span>
            <br />
            <span>
              {t("features.emailSetting.inProgess.newEmail")}"
              {state.email_confirm}"
            </span>
          </p>

          <p className="mt-3">
            {t("features.emailSetting.inProgess.emailChangeLinkInstruction")}
            <br />
            {t("features.emailSetting.inProgess.completionDeadline")}
          </p>

          <p className="mt-3">
            {t("features.emailSetting.noConfirmationEmailGuidance")}
            <br />
            {t("features.emailSetting.emailDelayNotice")}
            <br />
            {t("features.emailSetting.emailSpamNotice")}
          </p>

          <hr />
        </div>
      )}
    </Box>
  );
};

export default EmailSetting;
